import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { useCallback, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import {
    WORKSPACE_MANUFACTURER_SELECTED_KEY,
    WORKSPACE_MANUFACTURERS_LISTING_ITEMS_KEY,
    WorkspaceManufacturerSelected,
} from 'shared/utils/workspaceUtils'
import { useElementItemStore } from '../../../ElementItemStore'
import {
    useSelectedProjectElementNode,
    useShallowSelector,
    useWorkspace2Store,
} from '../../../Workspace2Store'
import { useProductFetcher } from '../../../api/useProductFetcher/useProductFetcher'
import { useTrackChangesQuery } from '../../../components/TrackChangesPanel/api'
import { ContentItemViewNodesType } from '../../../types'
import {
    findBodParagraph,
    findManufacturerParagraph,
    isSectionActive,
    syncVisibilityBetweenBODAndManufacturers,
} from '../../../utils/bodUtils'
import { findRootArticle } from '../../../utils/contentItemViewNodesUtils'
import { updateBestPracticesForContentItem } from '../../bestPractices'
import { HyperlinkParentNode } from '../types'
import { extractElementIds } from './utils/elementIdExtractor'

/**
 * Delay in milliseconds before firing getContentItemViewNodesAPI after a dialog-mutation
 * SW message arrives. The dialog already awaits the first mutation before sending the
 * message, so the backend has committed the change; this small buffer covers the second
 * mutation (addListingAsBOD / addManufacturerProductListingInProject) settling on the
 * server before we re-fetch the content view.
 */
const POST_MUTATION_REFRESH_DELAY_MS = 1500

/**
 * Updates node selection state for hyperlink elements.
 *
 * `isBOD` scopes the match to the correct pill type. BOD pill copies live as separate
 * nodes (id contains `_bod_`) but share the same Hyperlink element id as the source
 * manufacturer pill. Without scoping, flipping isPillSelected for one listingId would
 * update BOTH the MFR pill and its BOD copy — which causes the most-recent add to
 * appear in both paragraphs. Pass `isBOD=true` to target BOD copies only, `false` to
 * target manufacturer pills only.
 */
const updateNodeSelectionState = (
    nodes: ContentItemViewNodesType[],
    listingId: string,
    isSelected: boolean,
    isBOD: boolean
): {
    updatedNodes: ContentItemViewNodesType[]
    hasChanges: boolean
    affectedNode: (HyperlinkParentNode & { id: string }) | null
} => {
    let hasChanges = false
    let affectedNode: (HyperlinkParentNode & { id: string }) | null = null

    const updatedNodes = nodes.map((node) => {
        // Scope by pill type — BOD copies have '_bod_' in their id, manufacturer pills
        // do not. Skip the node entirely if it's the wrong type for this add.
        const isBodPillNode = node.id?.includes('_bod_') === true
        if (isBodPillNode !== isBOD) {
            return node
        }

        // Check if this node has a hyperlink element with matching pillId or id
        const nodeWithPill = (node as unknown) as HyperlinkParentNode
        const hasMatchingHyperlink = node.elements?.some((el) => {
            return (
                el.type === 'Hyperlink' &&
                (nodeWithPill.pillId === listingId || el.id === listingId)
            )
        })

        if (hasMatchingHyperlink) {
            const nodeWithPill = node as ContentItemViewNodesType &
                HyperlinkParentNode

            // Check if the selection state is actually changing
            if (nodeWithPill.isPillSelected !== isSelected) {
                hasChanges = true
                if (node.id) {
                    affectedNode = {
                        ...nodeWithPill,
                        id: node.id,
                    } as HyperlinkParentNode & { id: string }
                }

                return {
                    ...node,
                    isPillSelected: isSelected,
                }
            }
        }

        return node
    })

    return { updatedNodes, hasChanges, affectedNode }
}

/**
 * Hook to listen for service worker messages and update hyperlink selection states
 */
export const useServiceWorkerListener = (
    selectedProjectElementNodeFromProps?: Partial<ProjectElementNode> | null
) => {
    const { projectId: projectIdFromParams } = useParams<{
        projectId?: string
    }>()
    const {
        contentItemViewNodes,
        setContentItemViewNodes,
    } = useShallowSelector((state) => ({
        contentItemViewNodes: state.contentItemViewNodes,
        setContentItemViewNodes: state.actions.setContentItemViewNodes,
    }))

    // Get element item store for clearing element value
    const elementItemValue = useElementItemStore((s) => s.elementItemValue)
    const setElementItemValue = useElementItemStore(
        (s) => s.setElementItemValue
    )

    // Use the prop if provided, otherwise fall back to the store
    const selectedProjectElementNodeFromStore = useSelectedProjectElementNode()

    // Check if prop is provided and not null/undefined, otherwise use store
    // Also check if store value is not an empty object
    const isStoreNodeEmpty =
        !selectedProjectElementNodeFromStore ||
        (typeof selectedProjectElementNodeFromStore === 'object' &&
            Object.keys(selectedProjectElementNodeFromStore).length === 0)

    const selectedProjectElementNode =
        selectedProjectElementNodeFromProps ||
        (!isStoreNodeEmpty ? selectedProjectElementNodeFromStore : null)

    // Get content view update functions from useProductFetcher
    // Use overrideContentItemNodes: false to merge content instead of replacing
    const { getContentItemViewNodesAPI } = useProductFetcher({
        projectId: projectIdFromParams || '',
        overrideContentItemNodes: false,
    })

    // Get track changes refresh function
    const { fetchTrackChangesData } = useTrackChangesQuery()

    // Simple race condition protection
    const operationInProgress = useRef(false)

    // Timeout ref for delayed API call
    const delayedApiTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    // Pending selection state from optimistic SW messages. Each dialog-mutation message adds an
    // entry here. When the delayed API refresh resolves, every pending listingId is re-applied
    // against fresh contentItemViewNodes so the backend's isPillSelected:false doesn't strip the
    // pill under the dynamic-ML filter. This survives multiple rapid adds — the prior closure
    // approach only captured the LAST message's listingId, dropping earlier adds.
    //
    // The map key is `${listingId}|${isBOD ? 'bod' : 'mfr'}` so adding the same listing as both
    // a manufacturer and a BOD doesn't collide — each pill type needs its own re-apply.
    const pendingSelectionsRef = useRef<
        Map<string, { listingId: string; isSelected: boolean; isBOD: boolean }>
    >(new Map())

    /**
     * Returns the current nodes from the store rather than the handler's closure, so a
     * burst of messages arriving before React re-renders each builds on the last result.
     * Falls back to the closure value when the store is unavailable.
     */
    const getLatestContentItemViewNodes = useCallback((): ContentItemViewNodesType[] => {
        const fromStore = useWorkspace2Store?.getState?.()?.contentItemViewNodes
        return (fromStore ??
            contentItemViewNodes) as ContentItemViewNodesType[]
    }, [contentItemViewNodes])

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (delayedApiTimeoutRef.current) {
                clearTimeout(delayedApiTimeoutRef.current)
                delayedApiTimeoutRef.current = null
            }
        }
    }, [])

    // Memoized message handler to avoid recreating on every render
    const handleServiceWorkerMessage = useCallback(
        async ({ data }: MessageEvent) => {
            // Validate message data structure
            if (!data || typeof data !== 'object') {
                return
            }

            // Simple protection against concurrent operations
            if (operationInProgress.current) {
                return
            }

            operationInProgress.current = true

            try {
                // Check for manufacturer selection state changes
                const workspaceManufacturerSelectedState = data?.[
                    WORKSPACE_MANUFACTURER_SELECTED_KEY
                ] as WorkspaceManufacturerSelected
                // Check for changes on listing card for Best Practices/Global Best Practices
                const seletectedListingForBP = data?.[
                    WORKSPACE_MANUFACTURERS_LISTING_ITEMS_KEY
                ] as WorkspaceManufacturerSelected

                // One snapshot of the current nodes for this message, shared by the
                // best-practices and manufacturer-selection branches below.
                const latestNodes = getLatestContentItemViewNodes()

                if (seletectedListingForBP) {
                    // update Manufacturer/BOD listings on workspace
                    const updatedNodes = updateBestPracticesForContentItem(
                        seletectedListingForBP?.nodeId!,
                        seletectedListingForBP?.listingId,
                        latestNodes,
                        seletectedListingForBP?.isBP ?? false,
                        seletectedListingForBP?.isGBP ?? false,
                        true
                    )
                    setContentItemViewNodes(updatedNodes)
                }
                // Extract elementId, nodeId, and productFamilyId using utility function
                const {
                    elementId: elementIdToUse,
                    nodeId: nodeIdFromMessage,
                    productFamilyId: productFamilyIdFromMessage,
                } = extractElementIds(data)

                if (
                    workspaceManufacturerSelectedState !== undefined &&
                    workspaceManufacturerSelectedState !== null
                ) {
                    const {
                        listingId,
                        isSelected,
                        isBOD,
                        isFromDialogMutation,
                        isOptimisticOnly,
                    } = workspaceManufacturerSelectedState

                    // Record this selection so the post-fetch re-apply covers it. Multiple rapid
                    // adds (e.g. BOD #1, BOD #2, then a manufacturer) each cancel the previous
                    // setTimeout — without this map we'd only remember the LAST listingId, and
                    // the earlier pills would come back from the backend with isPillSelected:false
                    // and get filtered out by the dynamic-ML filter.
                    if (isFromDialogMutation && listingId) {
                        const pendingKey = `${listingId}|${
                            isBOD ? 'bod' : 'mfr'
                        }`
                        pendingSelectionsRef.current.set(pendingKey, {
                            listingId,
                            isSelected,
                            isBOD: !!isBOD,
                        })
                    }

                    // Update contentItemViewNodes to reflect the selection state. Scope by pill
                    // type so adding as BOD only flips BOD copies, and adding as MFR only flips
                    // manufacturer pills — they share Hyperlink ids and must not bleed into
                    // each other's paragraph.
                    let {
                        updatedNodes,
                        hasChanges,
                        affectedNode,
                    } = updateNodeSelectionState(
                        latestNodes,
                        listingId,
                        isSelected,
                        !!isBOD
                    )

                    // When a card is added as BOD (isBOD: true, isSelected: true),
                    // enable the BOD paragraph and BOD pills
                    if (isBOD && isSelected) {
                        // Find the BOD paragraph node to use as reference
                        const bodParagraph = findBodParagraph(updatedNodes)

                        if (bodParagraph) {
                            // First-add guard (symmetric to manufacturer add): only auto-hide the
                            // manufacturer paragraph when the BOD section was not yet active.
                            const bodWasActiveBefore = isSectionActive(
                                bodParagraph
                            )
                            // Use syncVisibilityBetweenBODAndManufacturers to enable BOD paragraph and pills
                            // shouldBeHidden: false means we're activating the BOD
                            updatedNodes = syncVisibilityBetweenBODAndManufacturers(
                                bodParagraph,
                                updatedNodes,
                                false // shouldBeHidden = false means activate
                            ) as ContentItemViewNodesType[]
                            if (!bodWasActiveBefore) {
                                const mfrParagraph = findManufacturerParagraph(
                                    updatedNodes
                                )
                                if (mfrParagraph) {
                                    updatedNodes = syncVisibilityBetweenBODAndManufacturers(
                                        mfrParagraph,
                                        updatedNodes,
                                        true
                                    ) as ContentItemViewNodesType[]
                                }
                            }
                            hasChanges = true
                        }
                    } else if (isBOD && !isSelected) {
                        // When a BOD is removed (isBOD: true, isSelected: false),
                        // hide the BOD paragraph and BOD pills
                        const bodParagraph = findBodParagraph(updatedNodes)

                        if (bodParagraph) {
                            updatedNodes = syncVisibilityBetweenBODAndManufacturers(
                                bodParagraph,
                                updatedNodes,
                                true // shouldBeHidden = true means deactivate
                            ) as ContentItemViewNodesType[]
                            hasChanges = true
                        }
                    } else if (!isBOD && isSelected) {
                        // When a manufacturer listing is added (isBOD: false,
                        // isSelected: true), activate the MFR section optimistically.
                        // Symmetric to the BOD activation above. Without this, the user
                        // sees no MFR section change until the delayed fetch lands.
                        //
                        // Reference detection: at PT level the workspace doesn't surface a
                        // separate manufacturer paragraph node — pills sit directly under
                        // the PT. At family level a mixed paragraph (isManufacturers && isBOD)
                        // exists. findManufacturerParagraph picks the mixed paragraph if present;
                        // otherwise the first MFR pill works as a reference for syncVisibility
                        // since the dynamic decision matrix only flips MFR-typed nodes for an MFR ref.
                        const mfrParagraph = findManufacturerParagraph(updatedNodes)

                        // Only auto-hide BOD on the FIRST listing added to the PT (i.e. when this
                        // add is what first activates the manufacturer section). Subsequent
                        // manufacturer adds must not touch the user's BOD visibility. The backend
                        // persists the same result, so the post-mutation refetch reconciles.
                        const mfrWasActiveBefore = isSectionActive(mfrParagraph)
                        if (mfrParagraph) {
                            updatedNodes = syncVisibilityBetweenBODAndManufacturers(
                                mfrParagraph,
                                updatedNodes,
                                false
                            ) as ContentItemViewNodesType[]
                            if (!mfrWasActiveBefore) {
                                const bodParagraph = findBodParagraph(
                                    updatedNodes
                                )
                                if (bodParagraph) {
                                    // Hide BOD paragraph + BOD pills (BOD ref + shouldBeHidden:true).
                                    // Dynamic matrix keeps MFR untouched (BOD→MFR_PARA: KEEP).
                                    updatedNodes = syncVisibilityBetweenBODAndManufacturers(
                                        bodParagraph,
                                        updatedNodes,
                                        true
                                    ) as ContentItemViewNodesType[]
                                }
                            }
                            hasChanges = true
                        }
                    }

                    // Update nodes if there were changes
                    if (hasChanges) {
                        setContentItemViewNodes(updatedNodes)
                    }

                    // Optimistic-only messages (sent immediately on add, before mutation) only
                    // do the in-memory paragraph/pill update. The post-mutation message fires
                    // the delayed content fetch once the backend has committed the change.
                    if (isOptimisticOnly) return

                    // Always refresh the content view and track changes after a manufacturer selection change
                    try {
                        // Ensure functions are available
                        if (
                            !getContentItemViewNodesAPI ||
                            !fetchTrackChangesData
                        ) {
                            return
                        }

                        // Use the nodeId from the message, or fall back to selectedProjectElementNode
                        let finalNodeId = nodeIdFromMessage

                        // If nodeId is not in the message, try to get it from selectedProjectElementNode
                        if (!finalNodeId && selectedProjectElementNode?.id) {
                            finalNodeId = selectedProjectElementNode.id
                        }

                        // The PLD's updatedInput.elementId is the newly created PT element ID —
                        // incompatible with getProjectProductFamily(View), which expects the
                        // family's elementId. Substitute productFamilyId whenever the family
                        // query/content fetch will fire. The selector keys off isProductTypeProject
                        // (useTrackChangesQuery: isProductTypeProject ? nugget : family), so gate on
                        // the SAME signal — not isProjectProductFamily, which is false when adding
                        // from assembly/family-group context yet the family query still fires,
                        // leaking the PT id into getProjectProductFamily ("elementId does not exist").
                        const isFamilyTrackChangesFetch = !elementItemValue.isProductTypeProject
                        const finalElementId =
                            isFamilyTrackChangesFetch &&
                            productFamilyIdFromMessage
                                ? productFamilyIdFromMessage
                                : elementIdToUse ||
                                  selectedProjectElementNode?.elementId ||
                                  null

                        // Update element item value
                        if (
                            (finalElementId &&
                                finalElementId !==
                                    elementItemValue.elementId) ||
                            (finalNodeId && finalNodeId !== elementItemValue.id)
                        ) {
                            setElementItemValue({
                                ...elementItemValue,
                                elementId:
                                    finalElementId ||
                                    elementItemValue.elementId,
                                id: finalNodeId || elementItemValue.id,
                            })
                        }

                        // Refresh content and track changes after 5 second delay
                        if (finalElementId && finalNodeId) {
                            // Find articles for content refresh
                            const currentNodes = (hasChanges
                                ? updatedNodes
                                : latestNodes) as ContentItemViewNodesType[]
                            let articleIds: string[] = []

                            if (affectedNode) {
                                const rootId = findRootArticle(
                                    (affectedNode as HyperlinkParentNode & {
                                        id: string
                                    }).id,
                                    currentNodes
                                )
                                if (rootId) articleIds = [rootId]
                            }

                            if (articleIds.length === 0) {
                                const articles = currentNodes.filter(
                                    (n) =>
                                        n.textType === 'Article' ||
                                        n.type === 'Article'
                                )
                                articleIds = articles
                                    .map((n) => n.id)
                                    .filter(Boolean) as string[]
                            }

                            // Skip the delayed API refresh ONLY when the optimistic update
                            // located the actual pill node for this listingId (affectedNode is
                            // truthy — updateNodeSelectionState returns it when a Hyperlink
                            // element with the matching listingId was found and isPillSelected
                            // was flipped).
                            //
                            // NOT enough to check hasChanges: the syncVisibilityBetweenBODAndManufacturers
                            // call below can flip a placeholder BOD paragraph's isHidden flag and
                            // set hasChanges=true even when the pill itself does NOT yet exist in
                            // contentItemViewNodes (e.g., adding BOD on a PT-not-in-project — the
                            // catalog content has a BOD paragraph placeholder but no pill nodes
                            // for the listingId). Skipping the fetch in that case leaves the pill
                            // permanently missing.
                            const pillFoundOptimistically = !!affectedNode
                            if (
                                !isFromDialogMutation ||
                                !pillFoundOptimistically
                            ) {
                                // Debounce within a burst: only a message that schedules a
                                // refresh may replace one already pending.
                                if (delayedApiTimeoutRef.current) {
                                    clearTimeout(delayedApiTimeoutRef.current)
                                    delayedApiTimeoutRef.current = null
                                }

                                // Shortened from 5s — the dialog already awaits the first
                                // mutation before sending the SW message, so the backend has
                                // committed the change. 1.5s leaves headroom for the second
                                // mutation (addListingAsBOD / addManufacturerProductListingInProject)
                                // to settle while keeping the user-perceived delay small.
                                delayedApiTimeoutRef.current = setTimeout(
                                    async () => {
                                        try {
                                            await getContentItemViewNodesAPI(
                                                articleIds.length > 0
                                                    ? articleIds
                                                    : null,
                                                undefined,
                                                finalElementId,
                                                finalNodeId || undefined
                                            )
                                            await fetchTrackChangesData(
                                                finalElementId
                                            )

                                            // The backend overwrites contentItemViewNodes with fresh
                                            // data where every newly-added pill has
                                            // isPillSelected:false. Re-apply the optimistic selection
                                            // for EVERY pending listingId (not just the last one) so
                                            // rapid sequential adds all become visible under the
                                            // dynamic-ML filter. Read fresh state via getState() —
                                            // the SW-handler closure captured stale nodes.
                                            if (
                                                pendingSelectionsRef.current
                                                    .size > 0
                                            ) {
                                                let nodes = useWorkspace2Store.getState()
                                                    .contentItemViewNodes as ContentItemViewNodesType[]
                                                let aggregateChanged = false
                                                pendingSelectionsRef.current.forEach(
                                                    (pending) => {
                                                        const {
                                                            updatedNodes: next,
                                                            hasChanges: changed,
                                                        } = updateNodeSelectionState(
                                                            nodes,
                                                            pending.listingId,
                                                            pending.isSelected,
                                                            pending.isBOD
                                                        )
                                                        if (changed) {
                                                            nodes = next
                                                            aggregateChanged = true
                                                        }
                                                    }
                                                )

                                                if (aggregateChanged) {
                                                    setContentItemViewNodes(
                                                        nodes
                                                    )
                                                }
                                                pendingSelectionsRef.current.clear()
                                            }
                                        } catch (error) {
                                            // Silent error - this is not critical to the selection operation
                                        }
                                    },
                                    POST_MUTATION_REFRESH_DELAY_MS
                                )
                            }
                        }
                    } catch (error) {
                        // Don't throw - this is not critical to the selection operation
                    }
                }
            } catch (error) {
                // Don't throw - this is not critical to the selection operation
            } finally {
                // Always clear the operation flag, even if there was an error
                operationInProgress.current = false
            }
        },
        [
            getLatestContentItemViewNodes,
            selectedProjectElementNode,
            elementItemValue,
            setElementItemValue,
            getContentItemViewNodesAPI,
            fetchTrackChangesData,
            setContentItemViewNodes,
        ]
    )

    useEffect(() => {
        const serviceWorker = navigator.serviceWorker

        if (!serviceWorker) {
            return
        }

        serviceWorker.addEventListener('message', handleServiceWorkerMessage)

        return () => {
            serviceWorker.removeEventListener(
                'message',
                handleServiceWorkerMessage
            )
        }
    }, [handleServiceWorkerMessage])
}
